import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Button } from '@command-center/ui-system';

export function useBulkSelection(ids: string[]) {
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const idKey = ids.join('\u0000');

  useEffect(() => {
    const allowed = new Set(ids);
    setSelected((current) => {
      const next = new Set([...current].filter((id) => allowed.has(id)));
      return next.size === current.size ? current : next;
    });
    // idKey is the stable value dependency for an often-recreated ids array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idKey]);

  const selectedIds = useMemo(() => [...selected], [selected]);
  const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
  return {
    selected,
    selectedIds,
    allSelected,
    toggle(id: string) {
      setSelected((current) => {
        const next = new Set(current);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    toggleAll() {
      setSelected(allSelected ? new Set() : new Set(ids));
    },
    clear() {
      setSelected(new Set());
    },
  };
}

export function SelectionCheckbox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <input
      type="checkbox"
      checked={checked}
      aria-label={label}
      className="h-4 w-4 shrink-0 cursor-pointer accent-primary"
      onClick={(event) => event.stopPropagation()}
      onChange={onChange}
    />
  );
}

export function BulkActionBar({
  count,
  busy,
  onClear,
  children,
}: {
  count: number;
  busy?: boolean;
  onClear: () => void;
  children: ReactNode;
}) {
  if (count === 0) return null;
  return (
    <div className="sticky top-2 z-20 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/30 bg-background/95 px-3 py-2 shadow-sm backdrop-blur">
      <span className="text-sm font-medium text-foreground">
        {count} {count === 1 ? 'record' : 'records'} selected
      </span>
      <div className="flex flex-wrap items-center gap-2">
        {children}
        <Button variant="ghost" size="sm" disabled={busy} onClick={onClear}>Clear</Button>
      </div>
    </div>
  );
}
